import styled, { css } from 'styled-components';
import Icon from '../../icon';
import { StyledListItem, StyledTypographyListItem } from '../../list';
import { TooltipIcon } from '../../tooltip';
import Typography from '../../typography';
import { pxToRem } from '../../../design-tokens/style-utilities';
export const formOptionCardItemContainerStyles = ({ theme, $disabled, $colorContext, }) => css `
	height: 100%;
	border-radius: ${pxToRem(theme.borderRadius.m)};
	position: relative;

	label::before {
		z-index: 1;
		content: '';
		position: absolute;
		inset: 0;
	}

	${TooltipIcon}, a {
		z-index: 2;
	}

	&:has(input:focus-visible) {
		outline: ${theme.borderWidth.m}px solid ${theme.colors.highlight.borderFocus};
		outline-offset: ${theme.borderWidth.m}px;

		${$colorContext === 'on-dark' &&
    css `
			outline: ${theme.borderWidth.m}px solid ${theme.colors.highlight.borderFocusOnDark};
		`};
	}

	${!$disabled &&
    css `
		&:hover {
			label {
				cursor: pointer;
			}
		}
	`}

	${$disabled &&
    css `
		&&& {
			background-color: ${$colorContext === 'on-medium'
        ? theme.colors.light.bgFormFieldDisabledOnMedium
        : theme.colors.extralight.bgFormFieldDisabled};

			* {
				color: ${$colorContext === 'on-medium'
        ? theme.colors.medium.textInactiveOnMedium
        : theme.colors.medium.textInactive};
				cursor: default;
			}

			& ${StyledListItem}::before, & ${StyledTypographyListItem}::before {
				background-color: ${$colorContext === 'on-medium'
        ? theme.colors.medium.iconInactiveOnMedium
        : theme.colors.medium.iconInactive};
			}
		}
	`}

	${$colorContext === 'on-light' &&
    css `
		background-color: ${theme.colors.extralight.bgFormField};
	`}

		${$colorContext === 'on-medium' &&
    css `
		background-color: ${theme.colors.blanc.bgFormField};
	`}

		${$colorContext === 'on-dark' &&
    css `
		background-color: ${theme.colors.blanc.bgFormField};
	`}
`;
export const FormOptionCardItemContainer = styled.div `
	${formOptionCardItemContainerStyles}
`;
/**
 * FormOptionCardItemWrapper
 */
export const formOptionCardItemWrapperStyles = ({ theme }) => css `
	background-color: ${theme.colors.blanc.bgFormField};
	border-radius: ${pxToRem(theme.borderRadius.m)};
`;
export const FormOptionCardItemWrapper = styled.div `
	${formOptionCardItemWrapperStyles}
`;
export const formOptionCardItemLabelStyles = ({ theme, $isInline, }) => css `
	/* Specificity added to combat form.styled.ts */
	&& {
		display: flex;
		flex-direction: column;
		gap: ${pxToRem(theme.spacing['300'])};
		height: 100%;
		padding: ${pxToRem(theme.spacing['400'])};

		${$isInline &&
    css `
			flex-direction: row-reverse;
			align-items: center;
			padding-top: ${pxToRem(theme.spacing['200'])};
			padding-bottom: ${pxToRem(theme.spacing['200'])};

			${FormOptionCardItemInnerContainer} {
				flex: 1;
			}
		`}
	}
`;
export const FormOptionCardItemLabel = styled.label `
	${formOptionCardItemLabelStyles}
`;
export const formOptionCardIconStyles = ({ theme }) => css `
	color: ${theme.colors.base.iconIllustrative};
`;
export const FormOptionCardIcon = styled(Icon) `
	${formOptionCardIconStyles}
`;
/**
 * FormOptionCardItemInnerContainer
 */
export const formOptionCardItemInnerContainerStyles = ({ theme }) => css `
	display: flex;
	gap: ${pxToRem(theme.spacing['200'])};
`;
export const FormOptionCardItemInnerContainer = styled.div `
	${formOptionCardItemInnerContainerStyles}
`;
export const formOptionCardItemTitleStyles = ({ theme, $hasTooltip, }) => css `
	margin-bottom: 0;

	${$hasTooltip &&
    css `
		&&& {
			margin-right: ${pxToRem(theme.spacing['400'])};
		}

		position: relative;

		${TooltipIcon} {
			position: absolute;
			line-height: inherit;
		}
	`}
`;
export const FormOptionCardItemTitle = styled(Typography).attrs(() => ({
    variant: 'component-subtitle',
})) `
	${formOptionCardItemTitleStyles}
`;
/**
 * FormOptionCardItemSubTitle
 */
export const formOptionCardItemSubTitleStyles = ({ theme }) => css `
	display: block;
	color: ${theme.colors.informative.textPrice};
`;
export const FormOptionCardItemSubTitle = styled(Typography) `
	${formOptionCardItemSubTitleStyles}
`;
/**
 * FormOptionCardItemSubTitlePostfix
 */
export const formOptionCardItemSubTitlePostfixStyles = ({ theme }) => css `
	display: inline-block;
	color: ${theme.colors.base.textPrice};
`;
export const FormOptionCardItemSubTitlePostfix = styled.span `
	${formOptionCardItemSubTitlePostfixStyles}
`;
export const formOptionCardItemSubTitleStrikeThroughStyles = ({ theme, $size, }) => css `
	position: relative;
	color: ${theme.colors.informative.textPrice};
	text-decoration: line-through;

	${$size === 'responsive' &&
    css `
		font-weight: normal;
	`}
`;
export const FormOptionCardItemSubTitleStrikeThrough = styled(Typography) `
	${formOptionCardItemSubTitleStrikeThroughStyles}
`;
export const formOptionCardItemContentStyles = ({ theme, $subTitleSize, }) => css `
	display: flex;
	flex-direction: column;
	align-items: flex-start;

	/* Slightly offset top to center title to radio input  */
	margin-top: ${pxToRem(2)};

	${$subTitleSize !== 'small' &&
    css `
		gap: ${pxToRem(theme.spacing['300'])};
	`}

	&& > * {
		margin: 0;

		& ${StyledListItem}:first-of-type, & ${StyledTypographyListItem}:first-of-type {
			padding-top: 0;

			&::before {
				top: 0;
			}
		}
	}
`;
export const FormOptionCardItemContent = styled.div `
	${formOptionCardItemContentStyles}
`;
/**
 * FormOptionCardItemSupportText
 */
export const formOptionCardItemSupportTextStyles = ({ theme }) => css `
	color: ${theme.colors.informative.textSupport};
`;
export const FormOptionCardItemSupportText = styled(Typography).attrs(() => ({
    variant: 'support-text',
})) `
	${formOptionCardItemSupportTextStyles}
`;
